import { get, post } from 'aws-amplify/api';

export class Genesys {
	static skillLabel = 'Queue';

	/** The list of required permissions for the telephony user to enable voice. */
	static requiredVoiceUserPermissions = [];

	/** The list of required permissions for the telephony user to enable SMS. */
	static requiredSmsUserPermissions = ['OAuth > Client > View'];

	/** The list of required permissions for the telephony user to enable email. */
	static requiredEmailUserPermissions = ['OAuth > Client > View'];

	/**
	 * Gets a list of registered email domains for the Genesys Cloud instance.
	 *
	 * @param {string} region The region of the Genesys Cloud instance.
	 * @param {string} token The client credentials token.
	 * @returns {Promise<string[]>} A list of the email domains for the Genesys Cloud instance.
	 */
	static async getEmailDomains(region, token) {
		try {
			const domainResponse = axios.get(
				`https://api.${region}/api/v2/routing/email/domains`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			return domainResponse.entities.map((entity) => entity.id);
		} catch (error) {
			console.error('Error getting email domains', error);
		}
	}

	/**
	 * Gets a list of registered SMS numbers for the Genesys Cloud instance.
	 *
	 * @param {string} token The JWT token for the current auth session.
	 * @param {string} apiKey The API key for the Lambda function.
	 * @returns {Promise<string[]>} A list of SMS phone numbers for the Genesys Cloud instance.
	 */
	static async getSmsNumbers(token, apiKey) {
		try {
			const smsNumbers = await API.get(
				'cdyxoutreach',
				'/cci/genesys/sms-numbers',
				{
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
				},
			);
			return smsNumbers;
		} catch (error) {
			console.error('Error getting SMS numbers', error);
		}
	}

	static async getQueues(token, apiKey) {
		try {
			const queues = await get({
				apiName: 'cdyxoutreach',
				path: '/cci/genesys/queues',
				options: {
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			return await queues.body.json();
		} catch (err) {
			console.error('Error getting queues', err);
		}
	}

	static async getCampaigns(token, apiKey) {
		try {
			const campaigns = await get({
				apiName: 'cdyxoutreach',
				path: '/cci/genesys/campaigns',
				options: {
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			return await campaigns.body.json();
		} catch (err) {
			console.error('Error getting queues', err);
		}
	}

	static async getContactLists(token, apiKey) {
		try {
			const contactLists = await get({
				apiName: 'cdyxoutreach',
				path: '/cci/genesys/contact-lists',
				options: {
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			return await contactLists.body.json();
		} catch (err) {
			console.error('Error getting contact lists', err);
		}
	}

	static async getScripts(token, apiKey) {
		try {
			const scripts = await get({
				apiName: 'cdyxoutreach',
				path: '/cci/genesys/scripts',
				options: {
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			return await scripts.body.json();
		} catch (err) {
			console.error('Error getting scripts', err);
		}
	}

	static async getCallAnalysisSets(token, apiKey) {
		try {
			const callAnalysisSets = await get({
				apiName: 'cdyxoutreach',
				path: '/cci/genesys/call-analysis-sets',
				options: {
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			return await callAnalysisSets.body.json();
		} catch (err) {
			console.error('Error getting call analysis sets', err);
		}
	}

	static async getEdgeGroups(token, apiKey) {
		try {
			const edgeGroups = await get({
				apiName: 'cdyxoutreach',
				path: '/cci/genesys/edge-groups',
				options: {
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			return await edgeGroups.body.json();
		} catch (err) {
			console.error('Error getting edge groups', err);
		}
	}

	static async getSites(token, apiKey) {
		try {
			const sites = await get({
				apiName: 'cdyxoutreach',
				path: '/cci/genesys/sites',
				options: {
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			return await sites.body.json();
		} catch (err) {
			console.error('Error getting sites', err);
		}
	}

	static async getWrapUps(token, apiKey, queue) {
		try {
			const wrapUps = await get({
				apiName: 'cdyxoutreach',
				path: `/cci/genesys/wrapups/${queue}`,
				options: {
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			return await wrapUps.body.json();
		} catch (err) {
			console.error('Error getting queues', err);
		}
	}

	static async createContactList(token, apiKey, name) {
		try {
			const contactList = await post({
				apiName: 'cdyxoutreach',
				path: '/cci/genesys/contact-lists',
				options: {
					headers: {
						Authorization: `Bearer ${token}`,
						'x-api-key': apiKey,
						'Content-Type': 'application/json',
					},
					body: {
						name: name,
					},
				},
			}).response;

			return await contactList.body.json();
		} catch (err) {
			console.error('Error creating contact list', err);
		}
	}
}
